class AppleProduct extends HTMLElement {
  constructor() {
    super();
    this.abortController = new AbortController();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  }

  connectedCallback() {
    const variantScript = this.querySelector('[data-product-variants]');

    try {
      this.variants = JSON.parse(variantScript?.textContent || '[]');
    } catch (_error) {
      this.variants = [];
    }

    this.variantIdInput = this.querySelector('[data-variant-id-input]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.addButton = this.querySelector('.apple-pdp__add');
    this.addButtonText = this.addButton?.querySelector('.add-to-cart-text__content span span');
    this.dock = this.querySelector('[data-mobile-dock]');
    this.dockButton = this.querySelector('[data-dock-submit]');
    this.purchaseCard = this.querySelector('[data-purchase-card]');
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';

    this.bindOptions();
    this.bindBundles();
    this.setupGallery();
    this.setupDock();
    this.setupReveal();
    this.updateShippingMessage();
    this.shippingTimer = window.setInterval(() => this.updateShippingMessage(), 60000);

    const selectedVariant = this.variants.find(
      (variant) => String(variant.id) === String(this.dataset.selectedVariantId),
    );

    if (selectedVariant) this.updateVariant(selectedVariant, { updateUrl: false, moveMedia: false });
  }

  disconnectedCallback() {
    this.abortController.abort();
    if (this.shippingTimer) window.clearInterval(this.shippingTimer);
    this.galleryObserver?.disconnect();
    this.dockObserver?.disconnect();
    this.revealObserver?.disconnect();
  }

  bindOptions() {
    const { signal } = this.abortController;

    this.querySelectorAll('[data-option-input]').forEach((input) => {
      input.addEventListener(
        'change',
        () => {
          const fieldset = input.closest('[data-option-index]');
          const selectedLabel = fieldset?.querySelector('[data-selected-option]');
          if (selectedLabel) selectedLabel.textContent = input.value;

          const selectedOptions = [...this.querySelectorAll('[data-option-index]')].map((group) => {
            return group.querySelector('[data-option-input]:checked')?.value;
          });

          const variant = this.variants.find((candidate) => {
            return candidate.options.every((option, index) => option === selectedOptions[index]);
          });

          this.updateVariant(variant || null, { updateUrl: true, moveMedia: true });
        },
        { signal },
      );
    });
  }

  bindBundles() {
    const { signal } = this.abortController;

    this.querySelectorAll('[data-bundle-input]').forEach((input) => {
      input.addEventListener(
        'change',
        () => {
          if (this.quantityInput) this.quantityInput.value = input.value;
        },
        { signal },
      );
    });
  }

  updateVariant(variant, options = {}) {
    const { updateUrl = true, moveMedia = true } = options;
    const available = Boolean(variant?.available);

    if (this.variantIdInput) {
      this.variantIdInput.value = variant?.id || '';
      this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (this.addButton) {
      this.addButton.disabled = !available;
      this.addButton.setAttribute('aria-disabled', String(!available));
    }

    if (this.dockButton) this.dockButton.disabled = !available;

    if (!variant) {
      if (this.addButtonText) this.addButtonText.textContent = 'Seçenek mevcut değil';
      return;
    }

    if (this.addButtonText) this.addButtonText.textContent = available ? 'Sepete ekle' : 'Tükendi';

    const priceText = this.formatMoney(variant.price);
    this.querySelectorAll('[data-product-price], [data-dock-price]').forEach((element) => {
      element.textContent = priceText;
    });

    const compareElement = this.querySelector('[data-product-compare]');
    const salePill = this.querySelector('[data-sale-pill]');
    const isSale = Number(variant.compare_at_price) > Number(variant.price);

    if (compareElement) {
      compareElement.textContent = isSale ? this.formatMoney(variant.compare_at_price) : '';
      compareElement.classList.toggle('is-hidden', !isSale);
    }

    if (salePill) {
      const saving = isSale
        ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
        : 0;
      salePill.textContent = isSale ? `%${saving} avantaj` : '';
      salePill.classList.toggle('is-hidden', !isSale);
    }

    this.updateBundlePrices(variant.price);
    this.updateAddToCartMedia(variant);

    if (moveMedia && variant.featured_media?.id) this.goToMedia(variant.featured_media.id);

    if (updateUrl && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  updateBundlePrices(unitPrice) {
    this.querySelectorAll('[data-bundle-input]').forEach((input) => {
      const quantity = Number(input.value) || 1;
      const discount = Number(input.dataset.discount) || 0;
      const price = unitPrice * quantity * ((100 - discount) / 100);
      const priceElement = input.closest('.apple-pdp__bundle')?.querySelector('[data-bundle-price]');
      if (priceElement) priceElement.textContent = this.formatMoney(Math.round(price));
    });
  }

  updateAddToCartMedia(variant) {
    const addComponent = this.querySelector('add-to-cart-component');
    const image = variant.featured_media?.preview_image?.src;
    if (addComponent && image) addComponent.dataset.productVariantMedia = image;
  }

  formatMoney(cents) {
    const value = Number(cents) || 0;
    const format = this.moneyFormat;
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount';
    let amount;

    switch (placeholder) {
      case 'amount_no_decimals':
        amount = this.formatNumber(value / 100, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = this.formatNumber(value / 100, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = this.formatNumber(value / 100, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = this.formatNumber(value / 100, 2, "'", '.');
        break;
      default:
        amount = this.formatNumber(value / 100, 2, ',', '.');
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  formatNumber(number, precision, thousands, decimal) {
    const fixed = Number(number).toFixed(precision);
    const [integer, fraction] = fixed.split('.');
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? `${grouped}${decimal}${fraction}` : grouped;
  }

  setupGallery() {
    this.galleryRail = this.querySelector('[data-gallery-rail]');
    this.gallerySlides = [...this.querySelectorAll('[data-media-id][data-slide-index].apple-pdp__slide')];
    this.galleryThumbs = [...this.querySelectorAll('[data-gallery-thumb]')];

    if (!this.galleryRail || this.gallerySlides.length < 2) return;

    const { signal } = this.abortController;

    this.galleryThumbs.forEach((thumb) => {
      thumb.addEventListener(
        'click',
        () => this.goToSlide(Number(thumb.dataset.slideIndex)),
        { signal },
      );
    });

    this.galleryRail.addEventListener(
      'keydown',
      (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const current = Number(this.querySelector('[data-gallery-thumb].is-active')?.dataset.slideIndex || 0);
        const next = Math.max(0, Math.min(this.gallerySlides.length - 1, current + direction));
        this.goToSlide(next);
      },
      { signal },
    );

    this.galleryObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        this.setActiveSlide(Number(visible.target.dataset.slideIndex));
      },
      { root: this.galleryRail, threshold: [0.55, 0.75, 0.95] },
    );

    this.gallerySlides.forEach((slide) => this.galleryObserver.observe(slide));
  }

  goToSlide(index) {
    const slide = this.gallerySlides?.[index];
    if (!slide || !this.galleryRail) return;

    this.galleryRail.scrollTo({
      left: slide.offsetLeft,
      behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
    });
    this.setActiveSlide(index);
  }

  goToMedia(mediaId) {
    const slide = this.gallerySlides?.find((candidate) => String(candidate.dataset.mediaId) === String(mediaId));
    if (slide) this.goToSlide(Number(slide.dataset.slideIndex));
  }

  setActiveSlide(index) {
    this.galleryThumbs?.forEach((thumb) => {
      const active = Number(thumb.dataset.slideIndex) === index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-selected', String(active));
      if (active) {
        const strip = thumb.parentElement;
        const left = thumb.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2;
        strip.scrollTo({ left, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      }
    });

    const counter = this.querySelector('[data-current-slide]');
    if (counter) counter.textContent = String(index + 1);
  }

  setupDock() {
    if (!this.addButton || !this.dock) return;

    const { signal } = this.abortController;

    this.dockButton?.addEventListener(
      'click',
      () => {
        if (!this.addButton.disabled) this.addButton.click();
      },
      { signal },
    );

    this.dockObserver = new IntersectionObserver(
      ([entry]) => {
        const isMobile = window.matchMedia('(max-width: 900px)').matches;
        const visible = isMobile && !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.dock.dataset.visible = String(visible);
        this.dock.setAttribute('aria-hidden', String(!visible));
      },
      { threshold: 0.1 },
    );

    this.dockObserver.observe(this.addButton);
  }

  setupReveal() {
    const revealItems = this.querySelectorAll('[data-reveal]');

    if (this.reducedMotion.matches || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-in-view'));
      return;
    }

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in-view');
          this.revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.18, rootMargin: '0px 0px -8% 0px' },
    );

    revealItems.forEach((item) => this.revealObserver.observe(item));
  }

  updateShippingMessage() {
    const message = this.querySelector('[data-shipping-message] span:last-child');
    if (!message) return;

    const cutoff = Number(this.dataset.shippingCutoff) || 16;
    const turkeyParts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Istanbul',
      weekday: 'short',
      hour: '2-digit',
      hour12: false,
    }).formatToParts(new Date());

    const day = turkeyParts.find((part) => part.type === 'weekday')?.value;
    const hour = Number(turkeyParts.find((part) => part.type === 'hour')?.value || 0);

    if (day === 'Sun') {
      message.textContent = 'Bugün sipariş ver, pazartesi kargoda.';
    } else if (day === 'Sat' && hour >= cutoff) {
      message.textContent = 'Şimdi sipariş ver, pazartesi kargoda.';
    } else if (hour < cutoff) {
      message.textContent = `${String(cutoff).padStart(2, '0')}:00'a kadar sipariş ver, bugün kargoda.`;
    } else {
      message.textContent = 'Şimdi sipariş ver, yarın kargoda.';
    }
  }
}

if (!customElements.get('apple-product')) {
  customElements.define('apple-product', AppleProduct);
}
